import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import { WtfDataColumn, WtfSyncBadge } from '../components/wtf';

export default function TLView() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyMetrics, setKeyMetrics] = useState({ visit_scheduled: 0, visit_done: 0, closure_expected: 0, closure_done: 0 });

  // WTF Live Data hooks
  // lead_stats: OBJECT {all_leads, todays_leads, converted_leads, total_walkins} (string values)
  // targets: ARRAY [{gym_id, gym_name, daily_target, daily_achieved, remaining_target}]
  // checkin_stats: OBJECT {total_member, total_trainer, total_guest} (string values)
  const { data: wtfLeadStats, lastSync: wtfSync, stale: wtfStale, loading: wtfLoading } = useWtfLiveData('lead_stats');
  const { data: wtfTargets } = useWtfLiveData('targets');
  const { data: wtfCheckins } = useWtfLiveData('checkin_stats');

  // Parse WTF lead stats (all values are strings)
  const wtfLeadData = useMemo(() => {
    if (!wtfLeadStats || typeof wtfLeadStats !== 'object' || Array.isArray(wtfLeadStats)) {
      return null;
    }
    return {
      todaysLeads: parseInt(wtfLeadStats.todays_leads || '0', 10),
      convertedLeads: parseInt(wtfLeadStats.converted_leads || '0', 10),
      totalWalkins: parseInt(wtfLeadStats.total_walkins_today || wtfLeadStats.total_walkins || '0', 10),
    };
  }, [wtfLeadStats]);

  // Parse WTF checkin stats (aggregate, not per-gym)
  const wtfCheckinData = useMemo(() => {
    if (!wtfCheckins || typeof wtfCheckins !== 'object' || Array.isArray(wtfCheckins)) {
      return null;
    }
    return {
      totalMembers: parseInt(wtfCheckins.total_member || '0', 10),
      totalTrainers: parseInt(wtfCheckins.total_trainer || '0', 10),
    };
  }, [wtfCheckins]);

  // Build per-gym target lookup from targets feed (array)
  const wtfTargetByGym = useMemo(() => {
    const lookup = {};
    if (Array.isArray(wtfTargets)) {
      wtfTargets.forEach(item => {
        if (item.gym_name) {
          lookup[item.gym_name.toLowerCase()] = {
            dailyTarget: item.daily_target || 0,
            dailyAchieved: item.daily_achieved || 0,
            remaining: item.remaining_target || 0,
          };
        }
      });
    }
    return lookup;
  }, [wtfTargets]);

  const fetchData = async () => {
    try {
      const [tlRes, metricsRes] = await Promise.all([
        axiosInstance.get('/views/tl', { params: { date } }),
        axiosInstance.get('/command-center', { params: { date } })
      ]);
      setData(tlRes.data);
      const glance = metricsRes.data?.today_at_glance || {};
      setKeyMetrics({
        visit_scheduled: glance.visit_scheduled || 0,
        visit_done: glance.visit_done || 0,
        closure_expected: glance.closure_expected || 0,
        closure_done: glance.closure_done || 0
      });
    } catch (error) {
      toast.error('Failed to load TL view');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  return (
    <div data-testid="tl-view-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Team Leader View
          <WtfSyncBadge lastSync={wtfSync} stale={wtfStale} size="sm" />
        </h1>
        <p className="page-subtitle">Performance overview by team leader</p>
      </div>

      <Card className="mb-6 p-6">
        <div className="flex gap-4 items-end">
          <div className="space-y-2 w-64">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="date-input"
            />
          </div>
        </div>
      </Card>

      {/* Key Sales Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-blue-600">{keyMetrics.visit_scheduled}</p>
            <p className="text-xs text-gray-600">Visit Scheduled</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{keyMetrics.visit_done}</p>
            <p className="text-xs text-gray-600">Visit Done</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{keyMetrics.closure_expected}</p>
            <p className="text-xs text-gray-600">Closure Expected</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-emerald-600">{keyMetrics.closure_done}</p>
            <p className="text-xs text-gray-600">Closure Done</p>
          </CardContent>
        </Card>
      </div>

      {/* WTF Live KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-medium uppercase">WTF Leads Today</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.todaysLeads ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-medium uppercase">WTF Walk-ins</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.totalWalkins ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-medium uppercase">WTF Converted</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.convertedLeads ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Team Leader</th>
                <th>Manager</th>
                <th>Gyms</th>
                <th>Dialed</th>
                <th>Connected</th>
                <th>Connect %</th>
                <th>Closures</th>
                <th style={{ color: '#9333ea' }}>WTF Target</th>
                <th style={{ color: '#9333ea' }}>WTF Achieved</th>
                <th>Verified</th>
              </tr>
            </thead>
            <tbody>
              {data.map((tl, idx) => (
                <tr key={idx} data-testid={`tl-row-${idx}`}>
                  <td className="font-semibold">{tl.tl_name}</td>
                  <td>{tl.manager}</td>
                  <td>{tl.gyms}</td>
                  <td>{tl.dialed}</td>
                  <td>{tl.connected}</td>
                  <td>
                    <span style={{ color: tl.connect_pct >= 70 ? '#16a34a' : tl.connect_pct >= 60 ? '#f59e0b' : '#dc2626' }}>
                      {tl.connect_pct}%
                    </span>
                  </td>
                  <td className="font-semibold">{tl.closures}</td>
                  <td>
                    {(() => {
                      // Match by gym names from targets feed
                      const gymNames = (tl.gym_names || []).map(n => n.toLowerCase());
                      const matched = gymNames.map(n => wtfTargetByGym[n]).filter(Boolean);
                      if (matched.length === 0) return <span className="text-gray-400 text-xs">--</span>;
                      const totalTarget = matched.reduce((s, g) => s + g.dailyTarget, 0);
                      return <span style={{ color: '#9333ea', fontWeight: 500 }}>{totalTarget}</span>;
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const gymNames = (tl.gym_names || []).map(n => n.toLowerCase());
                      const matched = gymNames.map(n => wtfTargetByGym[n]).filter(Boolean);
                      if (matched.length === 0) return <span className="text-gray-400 text-xs">--</span>;
                      const totalAchieved = matched.reduce((s, g) => s + g.dailyAchieved, 0);
                      return <span style={{ color: '#9333ea', fontWeight: 500 }}>{totalAchieved}</span>;
                    })()}
                  </td>
                  <td>
                    <span className="status-badge success">{tl.verified_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
